import React, { FC, useCallback, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';

import { ArrowShapeDownToLine, Shield } from '@gravity-ui/icons';
import CircleXmark from '@gravity-ui/icons/CircleXmark';
import FileArrowUp from '@gravity-ui/icons/FileArrowUp';
import FileQuestion from '@gravity-ui/icons/FileQuestion';
import { Alert, Icon, RadioButton, useToaster } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import clx from 'classnames';
import { installableAppUploadImage } from 'client/api/appStoreApi';
import { Field } from 'client/blocks-redesign/form/Field/Field';
import { FormErrors } from 'client/hocs/types';
import { Channel, InstallableApp } from 'client/model/skill';
import { InstallableAppFormSettings } from 'client/types';
import { showAppBackgroundImageField } from 'client/utils';
import { translateError } from 'client/utils/errorMessage';
import { ACCEPTED_LOGO_FORMATS } from 'client/utils/settings/common';
import * as appFormKeyset from 'i18n/AppForm';
import * as keyset from 'i18n/LogoUploader';
import * as settingsKeyset from 'i18n/Settings';

import styles from './FilesUploaders.module.scss';

const BACKGROUND_UPLOAD_ERROR = 'BACKGROUND_UPLOAD_ERROR';

const BackgroundPreview = {
    Page: 'Page',
    OnlyImage: 'OnlyImage',
} as const;

type TBackgroundPreview = (typeof BackgroundPreview)[keyof typeof BackgroundPreview];

interface BackgroundImageUploaderProps {
    aliceApp: InstallableApp;
    disabled: boolean;
    values: InstallableAppFormSettings;

    onIdChange?: (logoUrl: string) => void;
    onUrlChange?: (logoUrl: string) => void;
    setErrors?: (errors: FormErrors<InstallableAppFormSettings & object>, cb?: () => void) => void;
}

interface ImagePreviewProps {
    values: InstallableAppFormSettings;
    previewType: TBackgroundPreview;
    isTvApp: boolean;
}

const t = i18n(keyset);
const tSettings = i18n(settingsKeyset);
const tAppForm = i18n(appFormKeyset);

const createAppLogo = (
    isLoading: boolean,
    isDragActive: boolean,
    channel: Channel,
    userFileName: string | undefined,
) => {
    const containerStyle = [styles.appIcon__container, styles.appIcon_empty, styles.appIcon_largeBorderStroke];

    if (isLoading)
        return (
            <div className={clx(...containerStyle, styles.appIcon_loading)}>
                <Icon data={FileQuestion} size={15} />
                <div className={clx(styles.appIcon__label, styles.appIcon__label_large)}>
                    {tSettings('icon-is-loading')}
                </div>
            </div>
        );

    if (isDragActive)
        return (
            <div className={clx(...containerStyle)}>
                <div>{tAppForm('load-here')}</div>
            </div>
        );

    if (userFileName)
        return (
            <div className={clx(...containerStyle)}>
                <div>
                    {userFileName.length > 30 ? `...${userFileName.slice(userFileName.length - 27)}` : userFileName}
                </div>
            </div>
        );

    const sizeLabel = channel === Channel.SmartScreenApp ? '1920x1600' : '3840x2160';

    return (
        <div className={clx(...containerStyle)}>
            <Icon data={FileArrowUp} size={15} />
            <div className={clx(styles.appIcon__label, styles.appIcon__label_large)}>
                {sizeLabel}
                <br />
                {tSettings('icon-click-to-load')}
            </div>
        </div>
    );
};

const ImagePreview: FC<ImagePreviewProps> = ({ values, previewType, isTvApp }) => {
    const logoUrl = values.squareLogoUrl || values.logoUrl;
    const labels = [values.ageRestriction, values.apkInfosWithTargets?.[0].versionName, values.brandVerificationWebsite]
        .filter(Boolean)
        .join(' • ');

    return (
        <div
            className={clx(
                styles.logoPreview__container,
                previewType === BackgroundPreview.Page && isTvApp && styles.logoPreview__container_tvPagePreview,
            )}
            style={values.backgroundImageUrl ? { backgroundImage: `url(${values.backgroundImageUrl})` } : {}}
        >
            {previewType === BackgroundPreview.Page && (
                <article className={styles.pagePreview__container}>
                    <div>
                        <header className={styles.pagePreview__header}>
                            <div
                                className={styles.pagePreview__image}
                                style={logoUrl ? { backgroundImage: `url(${logoUrl})` } : {}}
                            />
                            <div>
                                <h4 className={styles.pagePreview__title}>{values.name}</h4>
                                <div className={styles.pagePreview__labels}>{labels}</div>
                            </div>
                        </header>
                        <div className={styles.pagePreview__description}>{values.description}</div>
                    </div>
                    <div className={styles.pagePreview__buttonsBlock}>
                        <div className={clx(styles.pagePreview__button, styles.pagePreview__button_action)}>
                            <Icon data={ArrowShapeDownToLine} size={6} />
                            {t('background-preview-setup-button')}
                        </div>
                        <div className={clx(styles.pagePreview__button, styles.pagePreview__button_idle)}>
                            <Icon data={Shield} size={6} />
                            {t('background-preview-condition-button')}
                        </div>
                    </div>
                </article>
            )}
        </div>
    );
};

export const BackgroundImageUploader: FC<BackgroundImageUploaderProps> = ({
    aliceApp,
    disabled,
    values,
    onIdChange,
    onUrlChange,
    setErrors,
}) => {
    const isTvApp = aliceApp.channel === Channel.TvApp;

    const [isLoading, setIsLoading] = useState(false);
    const [previewType, setPreviewType] = useState<TBackgroundPreview>(
        isTvApp ? BackgroundPreview.Page : BackgroundPreview.OnlyImage,
    );
    const [userFileName, setUserFileName] = useState<string>();

    const { add: addToast } = useToaster();

    useEffect(() => {
        let timer: ReturnType<typeof setTimeout>;
        if (userFileName) {
            timer = setTimeout(() => {
                setUserFileName(undefined);
            }, 1500);
        }
        return () => clearTimeout(timer);
    }, [userFileName]);

    const onDrop = useCallback((acceptedFiles) => {
        if (!acceptedFiles.length) return;

        const file = acceptedFiles[0];
        setIsLoading(true);

        installableAppUploadImage(aliceApp.id, aliceApp.channel, file, 'appTileImage')
            .then((image) => {
                setUserFileName(file.name);
                onIdChange && onIdChange(image.id);
                onUrlChange && onUrlChange(image.url);
                setIsLoading(false);
            })
            .catch((err) => {
                const { message } = err;
                const translatedErrorMessage = translateError(message);
                setErrors && setErrors({ logoId: translatedErrorMessage });
                addToast({
                    title: t('logo-uploader-error'),
                    content: translatedErrorMessage,
                    theme: 'danger',
                    renderIcon: CircleXmark,
                    name: BACKGROUND_UPLOAD_ERROR,
                });
                setUserFileName(undefined);
                setIsLoading(false);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        disabled,
        accept: [...ACCEPTED_LOGO_FORMATS],
    });

    if (!showAppBackgroundImageField()) return null;

    return (
        <div className={styles.coverUploaderContainer}>
            <Field
                fieldTitle={t('background-title')}
                fieldDescription={t('background-description')}
                containerClassName={clx(
                    styles.coverUploaderFormContainer,
                    styles.coverUploaderFormContainer_minMarginBottom,
                )}
                contentClassName={styles.coverUploaderFormContent}
            >
                {isTvApp && (
                    <>
                        <RadioButton
                            className={styles.selectLogoPreviewRadio}
                            options={[
                                { value: BackgroundPreview.Page, content: t('background-preview-option') },
                                { value: BackgroundPreview.OnlyImage, content: t('background-only-picture-option') },
                            ]}
                            onUpdate={(value) => setPreviewType(value)}
                            value={previewType}
                            size="l"
                        />
                        <Alert
                            theme="info"
                            title={
                                <span className={styles.logoUploaderAlert__title}>{t('check-background-title')}</span>
                            }
                            message={
                                <span className={styles.logoUploaderAlert__message}>{t('background-disclaimer')}</span>
                            }
                        />
                    </>
                )}
            </Field>
            <div className={styles.logoUploaderContainer}>
                {!disabled && (
                    <div
                        className={clx(styles.logoDropzone, disabled && styles.logoDropzone_disabled)}
                        {...getRootProps()}
                    >
                        <input {...getInputProps()} />
                        {createAppLogo(isLoading, isDragActive, aliceApp.channel, userFileName)}
                    </div>
                )}
                <ImagePreview values={values} previewType={previewType} isTvApp={isTvApp} />
            </div>
        </div>
    );
};
